// Consolidated financials for Sattva Developers Private Limited, keyed to the
// two source documents:
//
//   FY22 / FY23 / FY24 — audited, "Consolidated Financials (Ind AS)" pack
//                        (balance sheet + P&L signed 05-Mar-2025)
//   FY25               — unaudited, "Limited Review Financial Information of
//                        the Sattva Sponsor" (condensed consolidated summary)
//
// Everything is in INR millions, exactly as printed. Nothing here is derived —
// financeAnalytics.js owns every ratio and roll-up so the numbers below stay
// auditable line-by-line against the PDFs.
//
// NOTE ON FY24: the limited-review pack restates a handful of FY24 comparatives
// by a few lakhs (see FY24_RESTATEMENTS). We keep the ORIGINAL audited FY24
// figures as the FY24 series so the FY22-FY24 trend is internally consistent,
// and surface the deltas separately rather than silently picking one.

export const COMPANY = {
  name: 'Sattva Developers Private Limited',
  cin: 'U70101WB2004PTC097736',
  basis: 'Consolidated · Ind AS',
  units: 'INR million',
  registeredOffice: 'No. 41, Netaji Subhas Road, Kolkata 700 001',
  corporateOffice: 'Salarpuria Windsor, Ulsoor Road, Bengaluru 560 042',
};

export const FISCAL_YEARS = ['FY22', 'FY23', 'FY24', 'FY25'];

export const YEAR_META = {
  FY22: { label: 'FY22', endedOn: 'March 31, 2022', audited: true, source: 'Consolidated financials (Ind AS)' },
  FY23: { label: 'FY23', endedOn: 'March 31, 2023', audited: true, source: 'Consolidated financials (Ind AS)' },
  FY24: { label: 'FY24', endedOn: 'March 31, 2024', audited: true, source: 'Consolidated financials (Ind AS)' },
  FY25: { label: 'FY25', endedOn: 'March 31, 2025', audited: false, source: 'Limited review (condensed consolidated)' },
};

// Every series is ordered [FY22, FY23, FY24, FY25].
export const BALANCE_SHEET = {
  // --- non-current assets ---
  propertyPlantEquipment: [219.17, 167.27, 161.15, 165.54],
  investmentProperty: [1430.35, 1557.35, 1586.39, 4116.48],
  investmentPropertyUnderDev: [6295.07, 4138.34, 4402.70, 743.17],
  goodwill: [480.90, 507.14, 480.90, 480.90],
  intangibleAssets: [235.14, 235.21, 235.15, 235.02],
  rightOfUseAsset: [47.29, 37.19, 27.10, 52.56],
  nonCurrentInvestments: [6502.35, 9598.65, 13413.25, 17868.03],
  otherNonCurrentFinancialAssets: [1424.52, 2150.73, 868.33, 646.58],
  deferredTaxAssets: [2.22, 22.67, 38.14, 54.58],
  otherNonCurrentAssets: [1080.70, 1100.71, 1133.55, 1410.31],
  totalNonCurrentAssets: [17717.71, 19515.26, 22346.66, 25773.17],

  // --- current assets ---
  inventories: [3310.83, 6730.63, 5928.50, 10007.52],
  currentInvestments: [0, 0, 73.84, 73.84],
  tradeReceivables: [838.32, 511.52, 840.68, 1230.81],
  cashAndCashEquivalents: [72.53, 312.60, 211.55, 107.86],
  otherBankBalances: [48.58, 164.38, 35.55, 73.59],
  loans: [1455.18, 593.35, 993.06, 1382.39],
  otherCurrentFinancialAssets: [4569.45, 5196.53, 6875.97, 8571.29],
  currentTaxAssets: [28.80, 26.61, 74.42, 147.38],
  otherCurrentAssets: [328.50, 244.46, 1305.54, 1412.89],
  totalCurrentAssets: [10652.19, 13780.08, 16339.11, 23007.57],

  totalAssets: [28369.90, 33295.34, 38685.77, 48780.74],

  // --- equity ---
  equityShareCapital: [7.55, 7.55, 7.55, 7.55],
  otherEquity: [12802.60, 16775.69, 20237.44, 24697.02],
  nonControllingInterest: [-238.65, -210.34, -347.22, -190.37],
  totalEquity: [12571.50, 16572.90, 19897.77, 24514.20],

  // --- non-current liabilities ---
  nonCurrentBorrowings: [10821.93, 6257.76, 7153.86, 5708.90],
  nonCurrentLeaseLiabilities: [77.30, 61.77, 46.54, 69.59],
  otherNonCurrentFinancialLiab: [490.04, 501.62, 438.94, 445.59],
  otherNonCurrentLiabilities: [22.77, 11.38, 56.86, 45.20],
  deferredTaxLiabilities: [0.21, 0.04, 0, 0.37],
  totalNonCurrentLiabilities: [11412.25, 6832.59, 7696.20, 6269.65],

  // --- current liabilities ---
  currentBorrowings: [1618.07, 3028.18, 2329.27, 5270.09],
  currentLeaseLiabilities: [15.40, 18.55, 18.03, 14.34],
  tradePayablesMsme: [40.57, 6.24, 13.37, 12.45],
  tradePayablesOthers: [752.40, 202.39, 370.29, 467.92],
  otherCurrentFinancialLiab: [614.48, 4518.65, 3932.56, 8174.56],
  otherCurrentLiabilities: [1330.95, 2113.79, 4417.79, 4028.58],
  provisions: [3.24, 1.98, 4.87, 2.93],
  currentTaxLiabilities: [11.04, 0.06, 5.62, 26.03],
  totalCurrentLiabilities: [4386.15, 9889.85, 11091.80, 17996.89],
};

export const PROFIT_AND_LOSS = {
  revenueFromOperations: [3313.76, 2451.75, 5089.88, 7321.98],
  otherIncome: [320.34, 584.92, 1412.91, 2156.18],
  totalIncome: [3634.10, 3036.67, 6502.79, 9478.16],

  projectDevelopmentExpenses: [2379.84, 3729.87, 1588.58, 2235.69],
  purchaseOfStockInTrade: [0, 64.30, 1.13, 241.74],
  costOfStoresMaterialSold: [11.59, 197.71, 15.60, 3952.69],
  changesInInventories: [-66.84, -2820.28, 796.20, -2726.41],
  employeeBenefitsExpenses: [199.16, 259.59, 301.67, 342.98],
  financeCosts: [209.05, 388.00, 713.88, 746.09],
  depreciationAndAmortization: [86.77, 71.88, 64.10, 99.70],
  otherExpenses: [420.75, 300.22, 695.25, 561.88],
  totalExpenses: [3240.32, 2191.29, 4176.40, 5454.36],

  profitBeforeTax: [393.78, 845.38, 2326.39, 4023.80],

  currentTax: [184.62, 125.66, 622.53, 772.30],
  taxForEarlierYears: [0.06, -2.10, 0.05, 0.17],
  excessShortProvisionEarlier: [1.09, 1.95, -32.36, 5.43],
  deferredTax: [8.60, -19.92, -16.78, -14.81],

  profitBeforeShareOfJv: [199.41, 739.79, 1752.95, 3260.71],
  shareOfProfitFromJv: [652.63, 3200.15, 1739.02, 1947.96],
  profitForTheYear: [852.04, 3939.94, 3491.97, 5208.67],

  otherComprehensiveIncome: [1.39, 7.81, -5.48, 5.17],
  totalComprehensiveIncome: [853.43, 3947.75, 3486.49, 5213.84],
  tciOwnersOfParent: [880.84, 3974.12, 3568.70, 4979.12],
  tciNonControllingInterests: [-27.41, -26.37, -82.21, 234.72],

  earningsPerShare: [1129.99, 5227.03, 4616.31, 6903.42],
};

// FY24 lines where the limited-review pack's "audited" comparative column
// differs from the FY24 audited statements themselves. All immaterial (largest
// gap 2.66 million), but an analyst should see them rather than have one
// silently win. Rendered on the Statements tab.
export const FY24_RESTATEMENTS = [
  { line: 'Property, plant and equipment', audited: 161.15, restated: 158.49 },
  { line: 'Investment property', audited: 1586.39, restated: 1589.04 },
  { line: 'Deferred tax assets', audited: 38.14, restated: 38.46 },
  { line: 'Total non-current assets', audited: 22346.66, restated: 22346.97 },
  { line: 'Total assets', audited: 38685.77, restated: 38686.08 },
  { line: 'Other non-current financial liabilities', audited: 438.94, restated: 438.93 },
  { line: 'Deferred tax liabilities', audited: 0, restated: 0.32 },
  { line: 'Total non-current liabilities', audited: 7696.20, restated: 7696.51 },
  { line: 'Cost of stores material sold', audited: 15.60, restated: 15.48 },
  { line: 'Changes in inventories', audited: 796.20, restated: 796.32 },
  { line: 'Total expenses', audited: 4176.40, restated: 4176.41 },
  { line: 'Profit before tax', audited: 2326.39, restated: 2326.38 },
  { line: 'Profit for the year', audited: 3491.97, restated: 3491.96 },
  { line: 'Total comprehensive income', audited: 3486.49, restated: 3486.46 },
];

// Row order + labels for the statement tables. `depth` drives indentation and
// `total` marks the emphasised subtotal rows.
export const BALANCE_SHEET_LAYOUT = [
  { type: 'header', label: 'ASSETS' },
  { type: 'header', label: 'Non-current assets', depth: 1 },
  { key: 'propertyPlantEquipment', label: 'Property, plant and equipment', depth: 2 },
  { key: 'investmentProperty', label: 'Investment property', depth: 2 },
  { key: 'investmentPropertyUnderDev', label: 'Investment property under development', depth: 2 },
  { key: 'goodwill', label: 'Goodwill', depth: 2 },
  { key: 'intangibleAssets', label: 'Intangible assets', depth: 2 },
  { key: 'rightOfUseAsset', label: 'Right of use asset', depth: 2 },
  { key: 'nonCurrentInvestments', label: 'Investments', depth: 3 },
  { key: 'otherNonCurrentFinancialAssets', label: 'Other non-current financial assets', depth: 3 },
  { key: 'deferredTaxAssets', label: 'Deferred tax assets', depth: 2 },
  { key: 'otherNonCurrentAssets', label: 'Other non-current assets', depth: 2 },
  { key: 'totalNonCurrentAssets', label: 'Total non-current assets', total: true },
  { type: 'header', label: 'Current assets', depth: 1 },
  { key: 'inventories', label: 'Inventories', depth: 2 },
  { key: 'currentInvestments', label: 'Investments', depth: 3 },
  { key: 'tradeReceivables', label: 'Trade receivables', depth: 3 },
  { key: 'cashAndCashEquivalents', label: 'Cash and cash equivalents', depth: 3 },
  { key: 'otherBankBalances', label: 'Bank balances other than cash', depth: 3 },
  { key: 'loans', label: 'Loans', depth: 3 },
  { key: 'otherCurrentFinancialAssets', label: 'Other financial assets', depth: 3 },
  { key: 'currentTaxAssets', label: 'Current tax assets (net)', depth: 2 },
  { key: 'otherCurrentAssets', label: 'Other current assets', depth: 2 },
  { key: 'totalCurrentAssets', label: 'Total current assets', total: true },
  { key: 'totalAssets', label: 'Total assets', total: true, grand: true },
  { type: 'header', label: 'EQUITY AND LIABILITIES' },
  { key: 'equityShareCapital', label: 'Equity share capital', depth: 2 },
  { key: 'otherEquity', label: 'Other equity', depth: 2 },
  { key: 'nonControllingInterest', label: 'Non-controlling interest', depth: 2 },
  { key: 'totalEquity', label: 'Total equity', total: true },
  { type: 'header', label: 'Non-current liabilities', depth: 1 },
  { key: 'nonCurrentBorrowings', label: 'Borrowings', depth: 3 },
  { key: 'nonCurrentLeaseLiabilities', label: 'Lease liabilities', depth: 3 },
  { key: 'otherNonCurrentFinancialLiab', label: 'Other financial liabilities', depth: 3 },
  { key: 'otherNonCurrentLiabilities', label: 'Other non-current liabilities', depth: 2 },
  { key: 'deferredTaxLiabilities', label: 'Deferred tax liabilities', depth: 2 },
  { key: 'totalNonCurrentLiabilities', label: 'Total non-current liabilities', total: true },
  { type: 'header', label: 'Current liabilities', depth: 1 },
  { key: 'currentBorrowings', label: 'Borrowings', depth: 3 },
  { key: 'currentLeaseLiabilities', label: 'Lease liabilities', depth: 3 },
  { key: 'tradePayablesMsme', label: 'Trade payables — micro & small enterprises', depth: 3 },
  { key: 'tradePayablesOthers', label: 'Trade payables — others', depth: 3 },
  { key: 'otherCurrentFinancialLiab', label: 'Other financial liabilities', depth: 3 },
  { key: 'otherCurrentLiabilities', label: 'Other current liabilities', depth: 2 },
  { key: 'provisions', label: 'Provisions', depth: 2 },
  { key: 'currentTaxLiabilities', label: 'Current tax liabilities (net)', depth: 2 },
  { key: 'totalCurrentLiabilities', label: 'Total current liabilities', total: true },
];

export const PROFIT_AND_LOSS_LAYOUT = [
  { type: 'header', label: 'Revenue' },
  { key: 'revenueFromOperations', label: 'Revenue from operations (gross)', depth: 2 },
  { key: 'otherIncome', label: 'Other income', depth: 2 },
  { key: 'totalIncome', label: 'Total income', total: true },
  { type: 'header', label: 'Expenses' },
  { key: 'projectDevelopmentExpenses', label: 'Project development expenses', depth: 2 },
  { key: 'purchaseOfStockInTrade', label: 'Purchase of stock in trade', depth: 2 },
  { key: 'costOfStoresMaterialSold', label: 'Cost of stores material sold', depth: 2 },
  { key: 'changesInInventories', label: 'Changes in inventories of FG, WIP & stock-in-trade', depth: 2 },
  { key: 'employeeBenefitsExpenses', label: 'Employee benefits expenses', depth: 2 },
  { key: 'financeCosts', label: 'Finance costs', depth: 2 },
  { key: 'depreciationAndAmortization', label: 'Depreciation and amortization', depth: 2 },
  { key: 'otherExpenses', label: 'Other expenses', depth: 2 },
  { key: 'totalExpenses', label: 'Total expenses', total: true },
  { key: 'profitBeforeTax', label: 'Profit before tax', total: true },
  { type: 'header', label: 'Tax expense' },
  { key: 'currentTax', label: 'Current tax', depth: 2 },
  { key: 'taxForEarlierYears', label: 'Tax for earlier years', depth: 2 },
  { key: 'excessShortProvisionEarlier', label: 'Excess / short provision of earlier years', depth: 2 },
  { key: 'deferredTax', label: 'Deferred tax', depth: 2 },
  { key: 'profitBeforeShareOfJv', label: 'Profit before share of associates & JVs', total: true },
  { key: 'shareOfProfitFromJv', label: 'Share of profit from associates & JVs (net)', depth: 2 },
  { key: 'profitForTheYear', label: 'Profit for the year', total: true, grand: true },
  { key: 'otherComprehensiveIncome', label: 'Other comprehensive income, net of tax', depth: 2 },
  { key: 'totalComprehensiveIncome', label: 'Total comprehensive income', total: true },
  { type: 'header', label: 'Attributable to' },
  { key: 'tciOwnersOfParent', label: 'Owners of the parent', depth: 2 },
  { key: 'tciNonControllingInterests', label: 'Non-controlling interests', depth: 2 },
  { key: 'earningsPerShare', label: 'Earnings per equity share (₹, basic & diluted)', depth: 2, raw: true },
];
